import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';

import {
  AutocompleteElement,
  TextFieldElement
} from 'components/muiInputWrappers';
import { FormItem, FormTitle, GenericModalV2 } from 'components/shared';
import { validationRules } from 'helpers/utils';
import ActionsButtons from 'components/shared/GenericModalV2/components/ActionsButtons';
import { KEYS } from 'helpers/react-query.config';
import AddButton from 'layouts/CatalogLayout/AddButton';
import { CreateProblemFlowRequest, UpdateProblemFlow } from 'api/process';
import { useApiContext } from 'context/ApiContext';

import { Payload, StatusFlowByID } from '../../types';
import StateFlowSelection from '../StateFlowSelection';
import StateFlowDataGrid from '../StateFlowDataGrid';
import { transformProblemFlowDataForAPI } from '../../utils';
import useGetStatusFlowById from '../../services/useGetStatusFlowById';
import useCreateNewStatus from '../../services/useCreateNewStatus';
import useUpdateProblemFlow from '../../services/useUpdateProblemFlow';
import useGetClientsWhitOutFlow from '../../services/useGetClientsWhitOutFlow';

type Props = {
  handleCloseStateFlowModal: () => void;
  isEditStateFlow: boolean;
  problemFlowId: number | null | undefined;
  isFromDuplication?: boolean;
};

type FieldValues = {
  statusFlowName: string;
  clientId: number;
};

const { validateSpaces } = validationRules();

function StateFlowTable({
  handleCloseStateFlowModal,
  isEditStateFlow,
  problemFlowId,
  isFromDuplication = false
}: Props) {
  const form = useForm<FieldValues>();
  const queryClient = useQueryClient();
  const { client: clientApi } = useApiContext();

  const { data: clients, isLoading: isClientsLoading } =
    useGetClientsWhitOutFlow();

  const { mutate: createNewStatus, isLoading: isCreateNewStatusMutating } =
    useCreateNewStatus();
  const { isLoading: isProblemFlowLoading, data: problemFlowByIdData } =
    useGetStatusFlowById({ statusFlowId: problemFlowId });

  // Obtener información del cliente actual cuando se está editando
  const { data: currentClientData } = useQuery(
    ['client', problemFlowByIdData?.clientId],
    () => clientApi.clients.getClientById(problemFlowByIdData?.clientId),
    {
      enabled: !!problemFlowByIdData?.clientId && isEditStateFlow,
      refetchOnWindowFocus: false
    }
  );

  // Combinar clientes sin flujo con el cliente actual si está editando
  const availableClients = useMemo(() => {
    if (!clients) return [];

    const clientsList = [...clients];

    // Si estamos editando y el cliente actual no está en la lista, agregarlo
    if (
      isEditStateFlow &&
      problemFlowByIdData?.clientId &&
      currentClientData?.data?.data
    ) {
      const currentClientId = problemFlowByIdData.clientId;
      const clientExists = clientsList.some((c) => c.id === currentClientId);

      if (!clientExists) {
        const clientInfo = currentClientData.data.data;
        clientsList.push({
          id: currentClientId,
          label: clientInfo.name || ''
        });
      }
    }

    return clientsList;
  }, [
    clients,
    isEditStateFlow,
    problemFlowByIdData?.clientId,
    currentClientData
  ]);

  const { mutate: updateProblemFlow, isLoading: isUpdateProblemFlowMutating } =
    useUpdateProblemFlow();

  const [isStatesSelectionModalOpen, setOpenStatesSelectionModal] =
    useState(false);
  const [isStateFlowEditModalOpen, setOpenStateFlowEditModal] = useState(false);
  const { t } = useTranslation('global');

  // Estado que contiene los flujos de estados configurados en la tabla
  const [statusFlowData, setStatusFlowData] = useState<StatusFlowByID[]>([]);

  // Estado inicial de los datos para comparación durante la edición
  const [initialStatusFlowData, setInitialStatusFlowData] = useState<Payload[]>(
    []
  );

  // Estado que contiene el registro seleccionado para edición
  const [statusFlowForEdit, setStatusFlowForEdit] = useState<Payload | null>(
    null
  );

  useEffect(() => {
    if (problemFlowByIdData && !isProblemFlowLoading) {
      setStatusFlowData(problemFlowByIdData?.data);

      // Guardar datos iniciales solo una vez durante la edición
      if (isEditStateFlow && !initialStatusFlowData?.length) {
        setInitialStatusFlowData(problemFlowByIdData?.data);
      }

      // Prellenar formulario solo si no es una duplicación
      if (!isFromDuplication) {
        form.setValue('statusFlowName', problemFlowByIdData.name);
        // Establecer clientId solo cuando los clientes disponibles estén cargados
        // para que el AutocompleteElement pueda hacer el match correctamente
        if (
          availableClients &&
          availableClients.length > 0 &&
          problemFlowByIdData.clientId
        ) {
          form.setValue('clientId', problemFlowByIdData.clientId);
        }
      }
    }
  }, [
    initialStatusFlowData?.length,
    form,
    isEditStateFlow,
    isProblemFlowLoading,
    problemFlowByIdData,
    isFromDuplication,
    availableClients
  ]);

  useEffect(() => {
    if (statusFlowForEdit) {
      setOpenStateFlowEditModal(true);
    }
  }, [statusFlowForEdit]);

  const handleCloseStatesSelectionModal = () => {
    setOpenStatesSelectionModal(false);
    setOpenStateFlowEditModal(false);
    setStatusFlowForEdit(null);
  };

  const handleAddState = (payload: Payload) => {
    setStatusFlowData((prevState) => [...prevState, payload]);
    handleCloseStatesSelectionModal();
  };

  const handleEditState = (payload: StatusFlowByID) => {
    setStatusFlowData((prevState) => {
      const updatedState = [...prevState];
      const index = updatedState.findIndex(
        (data) => data.statusId === payload.statusId
      );

      if (index !== -1) {
        updatedState[index] = {
          ...payload,
          statusNumberOfChanges: payload.statusSelected?.length || 0
        };
      }

      return updatedState;
    });

    handleCloseStatesSelectionModal();
  };

  /**
   * Elimina un estado del flujo por su ID
   * Compara tanto statusId (StatusFlowByID) como id (Payload) para asegurar que se elimine correctamente
   */
  const handleRemoveStateFlow = (id: number) => {
    if (!id) return;

    setStatusFlowData((prevState) =>
      prevState.filter((item) => {
        // Obtener el ID del item (puede estar en statusId para StatusFlowByID o id para Payload)
        // Usar type assertion solo cuando sea necesario para acceder a propiedades específicas
        const statusId = 'statusId' in item ? item.statusId : undefined;
        const payloadId = 'id' in item ? item.id : undefined;
        const itemId = statusId ?? payloadId;

        // Mantener solo los items cuyo ID no coincide con el ID a eliminar
        return itemId !== id;
      })
    );
  };

  const handleStateFlowPost = (fieldValues: FieldValues) => {
    const payload: CreateProblemFlowRequest = {
      name: fieldValues.statusFlowName,
      clientId: fieldValues.clientId,
      edges: transformProblemFlowDataForAPI(statusFlowData)
    };

    createNewStatus(payload, {
      onSuccess: async () => {
        handleCloseStateFlowModal();
        form.reset({});
        await queryClient.invalidateQueries([
          KEYS.process.problemFlow.generalSearch()
        ]);
      }
    });
  };

  const handleStateFlowPut = (fieldValues: FieldValues) => {
    const payload: UpdateProblemFlow = {
      problemFlowId,
      name: fieldValues.statusFlowName,
      clientId: fieldValues.clientId,
      enabled: true,
      edges: transformProblemFlowDataForAPI(statusFlowData)
    };

    updateProblemFlow(payload, {
      onSuccess: () => {
        handleCloseStateFlowModal();
      }
    });
  };

  return (
    <>
      <GenericModalV2
        isOpen={isStateFlowEditModalOpen || isStatesSelectionModalOpen}
        onClose={handleCloseStatesSelectionModal}
        style={{ height: '100%' }}
        title={
          isStatesSelectionModalOpen
            ? t('problems.statusFlows.modals.addRegister')
            : t('problems.statusFlows.modals.editRegister')
        }
      >
        <StateFlowSelection
          dataForTheTable={statusFlowData}
          handleAddState={handleAddState}
          handleEditState={handleEditState}
          isBase={problemFlowByIdData?.base}
          registerForEdit={statusFlowForEdit}
        />
      </GenericModalV2>
      <Grid data-testid="state-flow-table" container>
        <Grid marginBottom={2} container>
          <Grid md={6} xs={12} item>
            <FormTitle gutterBottom>
              * {t('common.commonProperties.nameSingular')}:
            </FormTitle>
            <TextFieldElement
              control={form.control}
              data-testid=""
              name="statusFlowName"
              size="small"
              validation={{
                maxLength: {
                  value: 50,
                  message: t('clients.nameValidation')
                },
                validate: validateSpaces
              }}
              variant="outlined"
              fullWidth
              required
            />
          </Grid>
          <Grid marginTop={3} md={6} paddingLeft={1} xs={12} container item>
            <Grid md={6} sx={{ marginTop: { xs: 1, md: 0 } }} xs={12} item>
              <AddButton
                disabled={isCreateNewStatusMutating}
                onClickAddButton={() => setOpenStatesSelectionModal(true)}
                title={t('clients.newRegister')}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid xs={12} item>
          <Grid
            sx={{
              height: { xs: 400, md: 500 },
              width: '100%'
            }}
          >
            <StateFlowDataGrid
              data={statusFlowData}
              handleRemoveStateFlow={handleRemoveStateFlow}
              isLoading={isProblemFlowLoading}
              setDataForEdit={setStatusFlowForEdit}
            />
          </Grid>
        </Grid>

        <Grid xs={12} item>
          <Grid
            sx={{
              marginTop: 2,
              width: '100%'
            }}
          >
            <FormTitle gutterBottom>
              * {t('common.commonProperties.client')}
            </FormTitle>
            <FormItem>
              <AutocompleteElement
                autocompleteProps={{
                  size: 'small'
                }}
                control={form.control}
                disabled={isClientsLoading}
                name="clientId"
                options={availableClients}
                matchId
                required
              />
            </FormItem>
          </Grid>
        </Grid>

        <div style={{ marginTop: '1rem', width: '100%' }}>
          <ActionsButtons
            isAcceptButtonDisabled={
              isCreateNewStatusMutating || isUpdateProblemFlowMutating
            }
            isCancelButtonDisabled={
              isCreateNewStatusMutating || isUpdateProblemFlowMutating
            }
            onAccept={
              isEditStateFlow
                ? form.handleSubmit(handleStateFlowPut)
                : form.handleSubmit(handleStateFlowPost)
            }
            onCancel={handleCloseStateFlowModal}
            sx={{ width: { xs: '100%', sm: '47%' } }}
          />
        </div>
      </Grid>
    </>
  );
}

export default StateFlowTable;
